/**
 * Report React Query Hooks
 *
 * Provides data fetching hooks for report-related queries.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { reportService } from '../services';
import type { ReportParams } from '@/shared/types';

/**
 * Query key factory for report queries
 */
export const reportKeys = {
  all: ['reports'] as const,
  spendingOverTime: (params: ReportParams) =>
    [...reportKeys.all, 'spending-over-time', params] as const,
  incomeVsExpense: (params: ReportParams) =>
    [...reportKeys.all, 'income-vs-expense', params] as const,
  categoryDrilldown: (params: ReportParams) =>
    [...reportKeys.all, 'category-drilldown', params] as const,
  netWorthTrend: (params: Omit<ReportParams, 'compare_start' | 'compare_end'>) =>
    [...reportKeys.all, 'net-worth-trend', params] as const,
  budgetVsActual: (params: ReportParams) =>
    [...reportKeys.all, 'budget-vs-actual', params] as const,
};

export function useSpendingOverTime(params: ReportParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: reportKeys.spendingOverTime(params),
    queryFn: () => reportService.getSpendingOverTime(params),
    staleTime: 60 * 1000,
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
  });
}

export function useIncomeVsExpense(params: ReportParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: reportKeys.incomeVsExpense(params),
    queryFn: () => reportService.getIncomeVsExpense(params),
    staleTime: 60 * 1000,
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
  });
}

export function useCategoryDrilldown(params: ReportParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: reportKeys.categoryDrilldown(params),
    queryFn: () => reportService.getCategoryDrilldown(params),
    staleTime: 60 * 1000,
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
  });
}

export function useNetWorthTrend(
  params: Omit<ReportParams, 'compare_start' | 'compare_end'>,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: reportKeys.netWorthTrend(params),
    queryFn: () => reportService.getNetWorthTrend(params),
    staleTime: 60 * 1000,
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
  });
}

export function useBudgetVsActual(params: ReportParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: reportKeys.budgetVsActual(params),
    queryFn: () => reportService.getBudgetVsActual(params),
    staleTime: 60 * 1000,
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
  });
}
